import React, { useRef, useEffect, useCallback } from 'react';

interface LoadedSprite {
  image: HTMLImageElement | null;
  frames: Array<{ x: number; y: number; w: number; h: number }>;
  ready: boolean;
}

interface SpriteAnimationProps {
  sprite: LoadedSprite;
  isPlaying?: boolean;
  frameInterval?: number; // milliseconds per frame
  scale?: number;
  idleFrame?: 'first' | 'last';
  onCycleComplete?: () => void;
  className?: string;
  style?: React.CSSProperties;
  delay?: number; // milliseconds to delay start of animation
  dataTestId?: string;
  refProp?: React.RefObject<HTMLDivElement>;
  onClick?: () => void;
}

export const SpriteAnimation = ({
  sprite,
  isPlaying = true,
  frameInterval = 83.33, // Default: ~12 FPS (1000/12)
  scale = 1,
  idleFrame = 'first',
  onCycleComplete,
  className,
  style,
  delay = 0,
  dataTestId,
  refProp,
  onClick,
}: SpriteAnimationProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameIndexRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef(0);
  const animationStartTimeRef = useRef(0);
  // Helper function to get the idle frame index
  const getIdleFrameIndex = useCallback(() => {
    if (sprite.frames.length === 0) return 0;
    return idleFrame === 'first' ? 0 : sprite.frames.length - 1;
  }, [sprite.frames.length, idleFrame]);

  // Reset frame index and animation start time when sprite changes
  useEffect(() => {
    frameIndexRef.current = 0;
    animationStartTimeRef.current = 0;
  }, [sprite]);

  // Reset animation start time when isPlaying changes to true
  useEffect(() => {
    if (isPlaying) {
      animationStartTimeRef.current = 0;
    }
  }, [isPlaying]);

  // Set canvas size when sprite changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sprite.ready || !sprite.image || sprite.frames.length === 0) return;

    const firstFrame = sprite.frames[0];
    canvas.width = Math.round(firstFrame.w * scale);
    canvas.height = Math.round(firstFrame.h * scale);
  }, [sprite, scale]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !sprite.ready || !sprite.image || sprite.frames.length === 0) return;

    // frameInterval is now passed as a prop (milliseconds per frame)

    const drawFrameAt = (index: number) => {
      const frame = sprite.frames[index % sprite.frames.length];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(sprite.image!, frame.x, frame.y, frame.w, frame.h, 0, 0, canvas.width, canvas.height);
    };

    const animate = (currentTime: number) => {
      // Initialize start time on first frame
      if (animationStartTimeRef.current === 0) {
        animationStartTimeRef.current = currentTime;
      }

      // Check if delay period has passed
      const elapsedTime = currentTime - animationStartTimeRef.current;
      if (elapsedTime < delay) {
        // Still in delay period, show idle frame
        const idleIndex = getIdleFrameIndex();
        frameIndexRef.current = idleIndex;
        drawFrameAt(idleIndex);
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      if (!isPlaying) {
        // While paused, show the specified idle frame
        const idleIndex = getIdleFrameIndex();
        frameIndexRef.current = idleIndex;
        drawFrameAt(idleIndex);
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      if (currentTime - lastFrameTimeRef.current >= frameInterval) {
        const currentIndex = frameIndexRef.current;
        drawFrameAt(currentIndex);

        const nextIndex = (currentIndex + 1) % sprite.frames.length;
        if (nextIndex === 0) {
          onCycleComplete?.();
        }
        frameIndexRef.current = nextIndex;
        lastFrameTimeRef.current = currentTime;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [sprite, isPlaying, frameInterval, onCycleComplete, getIdleFrameIndex, delay]);

  // Draw idle frame when ready and not playing
  useEffect(() => {
    if (!sprite.ready || !sprite.image || sprite.frames.length === 0 || isPlaying) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const idleIndex = getIdleFrameIndex();
    frameIndexRef.current = idleIndex;
    const idleFrameData = sprite.frames[idleIndex];
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      sprite.image,
      idleFrameData.x,
      idleFrameData.y,
      idleFrameData.w,
      idleFrameData.h,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [sprite, isPlaying, getIdleFrameIndex]);

  return (
    <div ref={refProp} className={className} data-testid={dataTestId} style={style} onClick={onClick}>
      <canvas ref={canvasRef} />
    </div>
  );
};
